import React, { useState, useCallback, useEffect } from "react";
import styles from "../../LoanRequestPage.module.scss";
import TabSelector from "../../../../../common/components/tab-selector/TabSelector";
import { BackIcon, ChervonLeft } from "../../../../../assets/icons";
import IdentityTab from "./DetailsTabs/IdentityTab";
import AddressTab from "./DetailsTabs/AddressTab";
import EmploymentDetailsTab from "./DetailsTabs/EmploymentDetailsTab";
import FinancialInformationTab from "./DetailsTabs/FinancialInformationTab";
import UIModal from "../../../../../common/components/UI/Modal/UIModal";
import { OrderType } from "../../../../../model/orders/orders";
import moment from "moment";
import { toast } from "react-toastify";
import LoanProductOtpForm from "../../../loan-products/otp-form/LoanProductOtpForm";
import CommonDialog from "../../../../../common/components/dialog/CommonDialog";
import { showLoader, hideLoader } from "../../../../../stores/slices/loader-slice";
import { useDispatch } from "react-redux";
import CommonLoadingSpinner from "../../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import {useGetNGNCreditInfoDetailQuery, useGetOrderOverviewQuery, useGetZMBCreditInfoDetailQuery } from "../../../../../service/order-service";
import { Order, OrderTypeEnum } from "../../../../../model/loan-request";
import { useConfigLayout } from "../../../../../hooks/useConfigLayout";
import { useApproveCreditCheckMutation } from "../../../../../service/approval-service";
import Button from "../../../../../common/components/xepp/button/Button";
import ConfirmationDialog from "../../../../../common/components/dialog/ConfirmationDialog";

type Props = {
	setOrderDetails: (value: null) => void;
	orderDetails: Order;
	refetchData: () => void;
	orderType: number;
};

const LoanRequestDetails = (props: Props) => {
	const { orderDetails, setOrderDetails, refetchData } = props;
	const [selectedTab, setSelectedTab] = useState(1);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [rejectReason, setRejectReason] = useState("");
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
	const [showConfirmationDialog, setShowConfirmationDialog ] = useState(false);
	const dispatch = useDispatch();
	// const [getCreditEngineScore, { data: creditEngineScore, isLoading: isCreditScoreLoading }] = useLazyGetCreditEngineScoreQuery();
	const [acceptLoanRequest, { isLoading: isAcceptLoading }] = useApproveCreditCheckMutation();
	// const [declineLoanRequest, { isLoading: isDeclineLoading }] = useDeclineLoanRequestMutation();

	const  { selectedCountry } = useConfigLayout();

	const { 
		data: orderOverviewData, 
		isLoading: isOrderOverviewLoading, 
		isError: isOrderOverviewError 
	} = useGetOrderOverviewQuery(orderDetails.orderId);

	const {data: zmbData = undefined, isLoading: zmbDataLoading } = useGetZMBCreditInfoDetailQuery({ CustomerId: orderDetails.customerId },
		 {
			skip: selectedCountry?.isoCode === 'NGN'
	})

	const {data: ngnData = undefined, isLoading: ngnDataLoading} = useGetNGNCreditInfoDetailQuery({ CustomerId: orderDetails.customerId },
		 {
			skip: selectedCountry?.isoCode === 'ZMB'
	})

    const data = zmbData ?? ngnData;

	const tabs = [
		{ id: 1, title: "Identity", onClick: () => setSelectedTab(1) },
		{ id: 2, title: "Address", onClick: () => setSelectedTab(2) },
		{ id: 3, title: "Employment Details", onClick: () => setSelectedTab(3) },
		{ id: 4, title: "Financial Information", onClick: () => setSelectedTab(4) },
	];

	const renderTabContent = () => {
		switch (selectedTab) {
			case 1:
				return <IdentityTab orderDetails={data} />;
			case 2:
				return <AddressTab orderDetails={data} />;
			case 3:
				return <EmploymentDetailsTab orderDetails={data} />;
			default:
				return <FinancialInformationTab orderDetails={data} />;
		}
	};

	const formatNumber = (num?: number) => {
		if (num === undefined || num === null) return "N/A";

		const value = num.toFixed(2);

		return value.replace(/\d(?=(\d{3})+\.)/g, "$&,");
	};

	const handleApiCall = async (actionCode: string | null, stepName = "", isAccept = false) => {
		dispatch(showLoader());
		setShowRejectModal(false);
	  
		try {
		  const data: any = {
			creditCheckId: orderDetails.orderId,
			isWorthy: true,
		  };
	  
		await acceptLoanRequest(data).unwrap();
		toast.success('Accept operation successful');
		setOrderDetails(null)

	  
		} catch (error: any) {
		  const errorMessage = error.data?.title || "Something went wrong. Please try again.";
		  toast.error(errorMessage);
		} finally {
		  dispatch(hideLoader());
		}
	  };

	

	const handleSubmit = async () => {
		if (!rejectReason) {
			toast.error("Please enter a reason for rejecting the loan request");
			return;
		}
		handleApiCall(null);
	};

	const resendOtp = async () => {
		handleApiCall(null);
	};

	const verifyOtp = async (otp: string, isAccept?: boolean) => {
		handleApiCall(otp, "verified", isAccept);
	};

	return (
		<section className={styles.loanRequestPage}>
			<div className={styles.pageTitle}>
				<div className={styles.icon} onClick={() => setOrderDetails(null)}>
					<BackIcon />
				</div>
			{orderOverviewData?.details.contactInformation.fullName || 'N/A'}
			</div>
			<div className={styles.contentWrapper}>
				<CommonLoadingSpinner loading={isOrderOverviewLoading}>
				<div className={styles.cardsContainer}>
				{(orderOverviewData?.details.orderType as number) !== OrderTypeEnum.Installment && <>
					<div className={`${styles.card} ${styles.totalRequestCard}`}>
						<div className={styles.cardHeader}>Total Cost per Kwh <span className={styles.cardBadge}>Weekly</span> </div>
						<div className={styles.cardBody}>
							<span>{formatNumber(orderOverviewData?.details.costOverview.totalAmount.amount)}</span>
							<span className={styles.currency}>{orderOverviewData?.details.costOverview.totalAmount.currency}</span>
						</div>
					</div>
					<div className={`${styles.card} ${styles.pendingRequestCard}`}>
						<div className={styles.cardHeader}>Actual Cost per kWh</div>
						<div className={styles.cardBody}>
							<span>{formatNumber(orderOverviewData?.details.payAsYouGoDetails?.tariffAmount?.amount)}</span>
							<span className={styles.currency}>{orderOverviewData?.details.payAsYouGoDetails?.tariffAmount?.currency}</span>
						</div>
					</div>
					<div className={`${styles.card} ${styles.approvedRequestCard}`}>
						<div className={styles.cardHeader}>Interest per Kwh</div>
						<div className={styles.cardBody}>
							<span>{formatNumber(orderOverviewData?.details.monthlyPaymentDetails?.interest.amount)}</span>
							<span className={styles.currency}>{orderOverviewData?.details.monthlyPaymentDetails?.interest.currency}</span>
						</div>
					</div>
				</> }
				{(orderOverviewData?.details.orderType as number) === OrderTypeEnum.Installment && <>
				 <div className={`${styles.card} ${styles.totalRequestCard}`}>
						<div className={styles.cardHeader}>Total Cost</div>
						<div className={styles.cardBody}>
							<span>{formatNumber(orderOverviewData?.details.costOverview.totalAmount.amount)}</span>
							<span className={styles.currency}>{orderOverviewData?.details.costOverview?.totalAmount.currency}</span>
						</div>
					</div>
					<div className={`${styles.card} ${styles.pendingRequestCard}`}>
						<div className={styles.cardHeader}>Actual cost</div>
						<div className={styles.cardBody}>
							<span>{formatNumber(orderOverviewData?.details.costOverview.productPrice.amount)}</span>
							<span className={styles.currency}>{orderOverviewData?.details.costOverview.productPrice.currency}</span>
						</div>
					</div>
					<div className={`${styles.card} ${styles.approvedRequestCard}`}>
						<div className={styles.cardHeader}>Interest</div>
						<div className={styles.cardBody}>
							<span>{formatNumber(orderOverviewData?.details.monthlyPaymentDetails?.interest.amount)}</span>
							<span className={styles.currency}>{orderOverviewData?.details.monthlyPaymentDetails?.interest.currency || 'NGN'}</span>
						</div>
					</div>
				</>}
					<div className={`${styles.card} ${styles.declinedRequestCard}`}>
						<div className={styles.cardHeader}>Application Date</div>
						<div className={`${styles.cardBody} ${styles.date}`}>
							<span>{orderOverviewData?.details.createdAt ? moment(orderOverviewData.details.createdAt).format("h:mm A") : 'N/A'}</span>
							<span>{orderOverviewData?.details.createdAt ? moment(orderOverviewData.details.createdAt).format("MMM DD, YYYY") : 'N/A'}</span>
						</div>
					</div>
				</div>
               </CommonLoadingSpinner>

				<div className={styles.actionButtons}>
					{selectedTab === 4 ? (
						<>
						  <Button onClick={() => setShowRejectModal(true)}
								disabled={false}
								type={'button'}
								variant="outline"
								text={props.orderType === 1 ? 'Reject Offer' : 'Reject Credit Check'} />
		
							<Button onClick={() => setShowConfirmationDialog(true)} type={'button'} disabled={isAcceptLoading} text={props.orderType === 1 ? 'Approve Offer' : 'Approve Credit Check'} />
						
						</>
					) : null}
				</div>

				<div className={styles.tabSelectorWrapper}>
					<div className={styles.tabContainer}>
						<TabSelector tabs={tabs} selectedTab={selectedTab} theme="yellow" />
					</div>
					<CommonLoadingSpinner loading={ngnDataLoading || zmbDataLoading}>
						{renderTabContent()}
					</CommonLoadingSpinner>
					<div className={styles.buttonsWrapper}>
						{selectedTab !== 1 ? (
							<Button variant="outline" type="button" text="Previous" onClick={() => setSelectedTab(selectedTab - 1)}/>
					
						) : (
							<div></div>
						)}
						{selectedTab !== 4 ? (
							<Button type="button" text="Next" onClick={() => setSelectedTab(selectedTab + 1)} />
						) : (
							<div></div>
						)}
					</div>
				</div>
			</div>

			{showRejectModal && (
				<UIModal title="Reject Loan Request" onClose={() => setShowRejectModal(false)} onSubmit={handleSubmit}>
					<div className={styles.rejectLoanRequest}>
						<p>Please enter the reason for rejecting the customer request</p>
						<textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}></textarea>
					</div>
				</UIModal>
			)}

			{rejectDialogOpen && (
				<CommonDialog>
					<LoanProductOtpForm onResendOtp={resendOtp} onVerifyOtp={verifyOtp} onCloseDialog={() => setRejectDialogOpen(!rejectDialogOpen)} />
				</CommonDialog>
			)}

			{showConfirmationDialog && (
						<ConfirmationDialog
							actionLoading={false}
							btnText="Proceed"
							onCloseDialog={() => {
								setShowConfirmationDialog(!showConfirmationDialog);
							}}
							onConfirm={()=>handleApiCall(null, "", true)}
							title={props.orderType === 1 ? 'Approve Offer' : 'Approve Credit Check'}
							description={`Are you sure you want to approve ${props.orderType === 1 ? 'offer' : 'credit check'}?`}
						/>
					)}

			{acceptDialogOpen && (
				<CommonDialog>
					<LoanProductOtpForm
						onResendOtp={resendOtp}
						onVerifyOtp={(otp: string) => verifyOtp(otp, true)}
						onCloseDialog={() => setAcceptDialogOpen(!acceptDialogOpen)}
					/>
				</CommonDialog>
			)}
		</section>
	);
};

export default LoanRequestDetails;
